import axios from "axios";
import API_ENDPOINTS from "../config/api";
import { useEffect, useState, useCallback } from "react";
import styled from "styled-components";

interface Props {
  setSnackbar: any;
}

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  margin-top: 2rem;
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    flex: 1;
  }
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TileElement = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

const SkeletonElement = styled.div`
  width: 200px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CreateButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: #013566;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 2rem;
  align-self: flex-start;

  &:hover {
    background-color: #012a4a;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ActionButton = styled.button<{ $variant?: "edit" | "delete" }>`
  padding: 0.5rem 1rem;
  border: 1px solid
    ${(props) => (props.$variant === "delete" ? "#dc3545" : "#013566")};
  border-radius: 6px;
  background-color: transparent;
  color: ${(props) => (props.$variant === "delete" ? "#dc3545" : "#013566")};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 0.5rem;

  &:hover {
    background-color: ${(props) =>
      props.$variant === "delete" ? "#dc3545" : "#013566"};
    color: white;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  position: relative;
  width: 800px;
  max-width: 90%;
  border-radius: 12px;
  background-color: white;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #013566;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #013566;
  }
`;

const ModalFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ModalLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
`;

const ModalInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const FileInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const ModalButton = styled.button<{ $variant?: "secondary" }>`
  padding: 0.75rem 1.5rem;
  border: ${(props) =>
    props.$variant === "secondary" ? "1px solid #013566" : "none"};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "transparent" : "#013566"};
  color: ${(props) => (props.$variant === "secondary" ? "#013566" : "white")};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$variant === "secondary" ? "#f8f9fa" : "#012a4a"};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ConfirmContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ConfirmText = styled.p`
  font-size: 1.1rem;
  color: #333;
  margin: 0;
`;

const ZipCodesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
  margin-top: 1rem;
`;

const ZipCodeItem = styled.div`
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #eee;
  font-size: 0.95rem;
  color: #333;
`;

const TestimonialsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 6px;
`;

const TestimonialCard = styled.div`
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 0.9rem;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TestimonialMetaRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  color: #013566;
  font-weight: 700;
  font-size: 0.95rem;
`;

const Badge = styled.span<{ $tone?: "success" | "muted" }>`
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.82rem;
  border: 1px solid
    ${(p) => (p.$tone === "success" ? "#b7ebc6" : "#d6e2ee")};
  background: ${(p) => (p.$tone === "success" ? "#eaf7ef" : "#eef3f8")};
  color: ${(p) => (p.$tone === "success" ? "#1d6b3a" : "#013566")};
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  min-height: 110px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.25rem 0;
  cursor: pointer;
  user-select: none;
  color: #013566;
  font-weight: 800;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#f8f9fa" : "#013566")};
  color: ${(props) => (props.$disabled ? "#6c757d" : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #012a4a;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PageNumberButton = styled.button<{ $active?: boolean }>`
  min-width: 36px;
  padding: 0.5rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$active ? "#013566" : "white")};
  color: ${(props) => (props.$active ? "white" : "#013566")};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$active ? "#012a4a" : "#f8f9fa")};
    color: ${(props) => (props.$active ? "white" : "#013566")};
  }
`;

const Verticals: React.FC<Props> = ({ setSnackbar }) => {
  const token = localStorage.getItem("token");
  const [verticals, setVerticals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showZipModal, setShowZipModal] = useState(false);
  const [zipVertical, setZipVertical] = useState<any>(null);
  const [editingVertical, setEditingVertical] = useState<any>(null);
  const [deletingVertical, setDeletingVertical] = useState<any>(null);
  const [bannerVertical, setBannerVertical] = useState<any>(null);
  const [subverticals, setSubverticals] = useState<any[]>([]);
  const [subverticalsLoading, setSubverticalsLoading] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingBanner, setAddingBanner] = useState(false);
  const [editingBanner, setEditingBanner] = useState(false);
  const [editingSubvertical, setEditingSubvertical] = useState<any>(null);
  const [bannerSaving, setBannerSaving] = useState(false);
  const [bannerName, setBannerName] = useState("");
  const [bannerVariant, setBannerVariant] = useState("");
  const [zipcodes, setZipcodes] = useState<any[]>([]);
  const [zipcodesLoading, setZipcodesLoading] = useState(false);
  const [zipcodesUploading, setZipcodesUploading] = useState(false);
  const [zipcodesDeleting, setZipcodesDeleting] = useState(false);
  const [zipcodesPage, setZipcodesPage] = useState(1);
  const [zipcodesTotalPages, setZipcodesTotalPages] = useState(1);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const [showTestimonialsModal, setShowTestimonialsModal] = useState(false);
  const [testimonialsVertical, setTestimonialsVertical] = useState<any>(null);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [testimonialSaving, setTestimonialSaving] = useState(false);
  const [testimonialDeletingIds, setTestimonialDeletingIds] = useState<
    Record<string, boolean>
  >({});
  const [editingTestimonial, setEditingTestimonial] = useState<any>(null);
  const [testimonialUpdating, setTestimonialUpdating] = useState(false);
  const [testimonialRating, setTestimonialRating] = useState("5");
  const [testimonialAuthor, setTestimonialAuthor] = useState("");
  const [testimonialDescription, setTestimonialDescription] = useState("");
  const [testimonialIsActive, setTestimonialIsActive] = useState(true);
  const [editTestimonialRating, setEditTestimonialRating] = useState("5");
  const [editTestimonialAuthor, setEditTestimonialAuthor] = useState("");
  const [editTestimonialDescription, setEditTestimonialDescription] =
    useState("");
  const [editTestimonialIsActive, setEditTestimonialIsActive] = useState(true);

  const fetchVerticals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.VERTICALS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVerticals(response.data || []);
    } catch (error) {
      console.error("Error fetching verticals:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVerticals();
  }, [fetchVerticals]);

  useEffect(() => {
    if (!showBannerModal || !bannerVertical?.id) {
      setSubverticals([]);
      setSubverticalsLoading(false);
      return;
    }
    const fetchSubverticals = async () => {
      setSubverticalsLoading(true);
      try {
        const response = await axios.get(
          API_ENDPOINTS.VERTICAL_SUBVERTICALS(bannerVertical.id.toString()),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setSubverticals(Array.isArray(response.data) ? response.data : []);
        console.log(response.data, "DATA");
      } catch (error) {
        console.error("Error fetching subverticals:", error);
        setSubverticals([]);
      } finally {
        setSubverticalsLoading(false);
      }
    };
    fetchSubverticals();
  }, [showBannerModal, bannerVertical?.id, token]);

  const capitalizeFirst = (str: string) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleCreate = async () => {
    const name = prompt("Enter vertical name:");
    if (!name || !name.trim()) {
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        API_ENDPOINTS.VERTICALS,
        { name: name.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSnackbar({
        isOpen: true,
        message: "Vertical created successfully!",
        type: "success",
      });
      fetchVerticals();
    } catch (error: any) {
      console.error("Error creating vertical:", error);
      setSnackbar({
        isOpen: true,
        message: error.response?.data?.message || "Failed to create vertical",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (vertical: any) => {
    setEditingVertical(vertical);
    setEditName(vertical.name || "");
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVertical || !editName.trim()) {
      return;
    }

    try {
      setSaving(true);
      await axios.put(
        API_ENDPOINTS.VERTICAL_DETAILS(editingVertical.id.toString()),
        { name: editName.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSnackbar({
        isOpen: true,
        message: "Vertical updated successfully!",
        type: "success",
      });
      setShowEditModal(false);
      setEditingVertical(null);
      setEditName("");
      fetchVerticals();
    } catch (error: any) {
      console.error("Error updating vertical:", error);
      setSnackbar({
        isOpen: true,
        message: error.response?.data?.message || "Failed to update vertical",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleManageBannerClick = (vertical: any) => {
    setBannerVertical(vertical);
    setShowBannerModal(true);
  };

  const handleDeleteClick = (vertical: any) => {
    setDeletingVertical(vertical);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingVertical) return;

    try {
      setDeleting(true);
      await axios.delete(
        API_ENDPOINTS.VERTICAL_DETAILS(deletingVertical.id.toString()),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSnackbar({
        isOpen: true,
        message: "Vertical deleted successfully!",
        type: "success",
      });
      setShowDeleteConfirm(false);
      setDeletingVertical(null);
      fetchVerticals();
    } catch (error: any) {
      console.error("Error deleting vertical:", error);
      setSnackbar({
        isOpen: true,
        message: error.response?.data?.message || "Failed to delete vertical",
        type: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeletingVertical(null);
  };

  const fetchZipCodes = useCallback(
    async (verticalId: number, page: number = 1) => {
      if (!verticalId) return;
      try {
        setZipcodesLoading(true);
        const response = await axios.get(
          API_ENDPOINTS.VERTICAL_ZIP_CODES(verticalId.toString(), page, 10),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        // Handle different response structures
        if (response.data?.data) {
          setZipcodes(
            Array.isArray(response.data.data) ? response.data.data : [],
          );
          if (response.data.last_page) {
            setZipcodesTotalPages(response.data.last_page);
          } else if (response.data.total_pages) {
            setZipcodesTotalPages(response.data.total_pages);
          }
        } else if (Array.isArray(response.data)) {
          setZipcodes(response.data);
          setZipcodesTotalPages(1);
        } else {
          setZipcodes([]);
          setZipcodesTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching zip codes:", error);
        setZipcodes([]);
        setZipcodesTotalPages(1);
      } finally {
        setZipcodesLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (showZipModal && zipVertical?.id) {
      setZipcodesPage(1);
      fetchZipCodes(zipVertical.id, 1);
    } else if (!showZipModal) {
      setZipcodes([]);
      setZipcodesPage(1);
      setCsvFile(null);
    }
  }, [showZipModal, zipVertical?.id, fetchZipCodes]);

  const handleZipCodesUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile || !zipVertical?.id) {
      return;
    }

    try {
      setZipcodesUploading(true);
      const formData = new FormData();
      formData.append("csv_file", csvFile);

      await axios.post(
        API_ENDPOINTS.VERTICAL_ZIP_CODES_UPLOAD(zipVertical.id.toString()),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setSnackbar({
        isOpen: true,
        message: "Zip codes uploaded successfully!",
        type: "success",
      });
      setCsvFile(null);
      // Reset file input
      const fileInput = document.getElementById(
        "csvFileInput",
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
      // Refetch zip codes
      fetchZipCodes(zipVertical.id, zipcodesPage);
    } catch (error: any) {
      console.error("Error uploading zip codes:", error);
      setSnackbar({
        isOpen: true,
        message: error.response?.data?.message || "Failed to upload zip codes",
        type: "error",
      });
    } finally {
      setZipcodesUploading(false);
    }
  };

  const handleZipCodesPageChange = (page: number) => {
    if (zipVertical?.id) {
      setZipcodesPage(page);
      fetchZipCodes(zipVertical.id, page);
    }
  };

  const handleDeleteZipCodes = async () => {
    if (!zipVertical?.id) return;
    if (
      !window.confirm(
        `Are you sure you want to delete all zip codes for "${capitalizeFirst(zipVertical.name || "")}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      setZipcodesDeleting(true);
      await axios.delete(
        API_ENDPOINTS.VERTICAL_ZIP_CODES_UPLOAD(zipVertical.id.toString()),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setSnackbar({
        isOpen: true,
        message: "Zip codes deleted successfully!",
        type: "success",
      });
      setZipcodesPage(1);
      fetchZipCodes(zipVertical.id, 1);
    } catch (error: any) {
      console.error("Error deleting zip codes:", error);
      setSnackbar({
        isOpen: true,
        message: error.response?.data?.message || "Failed to delete zip codes",
        type: "error",
      });
    } finally {
      setZipcodesDeleting(false);
    }
  };

  const handleAddBannerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bannerName.trim() || !bannerVariant.trim() || !bannerVertical?.id) {
      return;
    }

    try {
      setBannerSaving(true);
      await axios.post(
        API_ENDPOINTS.VERTICAL_SUBVERTICALS(bannerVertical.id.toString()),
        {
          name: bannerVariant.trim(),
          banner: bannerName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSnackbar({
        isOpen: true,
        message: "Banner added successfully!",
        type: "success",
      });
      setBannerName("");
      setBannerVariant("");
      setAddingBanner(false);

      const response = await axios.get(
        API_ENDPOINTS.VERTICAL_SUBVERTICALS(bannerVertical.id.toString()),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setSubverticals(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error("Error adding banner:", error);
      setSnackbar({
        isOpen: true,
        message: error.response?.data?.message || "Failed to add banner",
        type: "error",
      });
    } finally {
      setBannerSaving(false);
    }
  };

  const handleEditBannerClick = (banner: any) => {
    setEditingSubvertical(banner);
    setBannerName(banner.banner ?? "");
    setBannerVariant(banner.name ?? "");
    setAddingBanner(true);
    setEditingBanner(true);
  };

  const handleAddBannerButtonClick = () => {
    setEditingSubvertical(null);
    setEditingBanner(false);
    setBannerName("");
    setBannerVariant("");
    setAddingBanner(true);
  };

  const handleEditBannerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !bannerName.trim() ||
      !bannerVariant.trim() ||
      !editingSubvertical?.name ||
      !bannerVertical?.id
    ) {
      return;
    }

    try {
      setBannerSaving(true);
      await axios.put(
        API_ENDPOINTS.VERTICAL_SUBVERTICAL_DETAILS(
          bannerVertical.id.toString(),
          editingSubvertical.name,
        ),
        {
          name: bannerVariant.trim(),
          banner: bannerName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSnackbar({
        isOpen: true,
        message: "Banner updated successfully!",
        type: "success",
      });
      setBannerName("");
      setBannerVariant("");
      setAddingBanner(false);
      setEditingBanner(false);
      setEditingSubvertical(null);

      const response = await axios.get(
        API_ENDPOINTS.VERTICAL_SUBVERTICALS(bannerVertical.id.toString()),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setSubverticals(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error("Error updating banner:", error);
      setSnackbar({
        isOpen: true,
        message: error.response?.data?.message || "Failed to update banner",
        type: "error",
      });
    } finally {
      setBannerSaving(false);
    }
  };

  const fetchTestimonials = useCallback(
    async (verticalId: number) => {
      if (!verticalId) return;
      try {
        setTestimonialsLoading(true);
        const resp = await axios.get(
          `https://api.whoffers.com/api/verticals/${verticalId}/testimonials`,
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : undefined,
        );
        const data = resp.data?.data ?? resp.data;
        setTestimonials(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error fetching testimonials:", e);
        setTestimonials([]);
      } finally {
        setTestimonialsLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (showTestimonialsModal && testimonialsVertical?.id) {
      fetchTestimonials(testimonialsVertical.id);
    } else if (!showTestimonialsModal) {
      setTestimonials([]);
      setTestimonialsLoading(false);
      setTestimonialsVertical(null);
      setTestimonialRating("5");
      setTestimonialAuthor("");
      setTestimonialDescription("");
      setTestimonialIsActive(true);
      setTestimonialSaving(false);
      setEditingTestimonial(null);
      setTestimonialUpdating(false);
      setEditTestimonialRating("5");
      setEditTestimonialAuthor("");
      setEditTestimonialDescription("");
      setEditTestimonialIsActive(true);
      setTestimonialDeletingIds({});
    }
  }, [showTestimonialsModal, testimonialsVertical?.id, fetchTestimonials]);

  const handleOpenTestimonials = (vertical: any) => {
    setTestimonialsVertical(vertical);
    setShowTestimonialsModal(true);
  };

  const handleCreateTestimonial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testimonialsVertical?.id) return;
    const ratingNum = Number(testimonialRating);
    if (!Number.isFinite(ratingNum) || ratingNum < 1 || ratingNum > 5) {
      setSnackbar({
        isOpen: true,
        message: "Rating must be between 1 and 5",
        type: "error",
      });
      return;
    }
    if (!testimonialDescription.trim() || !testimonialAuthor.trim()) {
      setSnackbar({
        isOpen: true,
        message: "Author and description are required",
        type: "error",
      });
      return;
    }

    try {
      setTestimonialSaving(true);
      await axios.post(
        "https://api.whoffers.com/api/testimonials",
        {
          rating: String(ratingNum),
          description: testimonialDescription.trim(),
          author: testimonialAuthor.trim(),
          is_active: testimonialIsActive ? 1 : 0,
          vertical_id: Number(testimonialsVertical.id),
        },
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          : {
              headers: {
                "Content-Type": "application/json",
              },
            },
      );

      setSnackbar({
        isOpen: true,
        message: "Testimonial created successfully!",
        type: "success",
      });

      setTestimonialRating("5");
      setTestimonialAuthor("");
      setTestimonialDescription("");
      setTestimonialIsActive(true);

      fetchTestimonials(Number(testimonialsVertical.id));
    } catch (error: any) {
      console.error("Error creating testimonial:", error);
      setSnackbar({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to create testimonial",
        type: "error",
      });
    } finally {
      setTestimonialSaving(false);
    }
  };

  const handleEditTestimonialClick = (t: any) => {
    if (!t) return;
    setEditingTestimonial(t);
    setEditTestimonialRating(String(t?.rating ?? "5"));
    setEditTestimonialAuthor(String(t?.author ?? ""));
    setEditTestimonialDescription(String(t?.description ?? ""));
    setEditTestimonialIsActive(!!t?.is_active);
  };

  const handleUpdateTestimonial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testimonialsVertical?.id || !editingTestimonial?.id) return;

    const ratingNum = Number(editTestimonialRating);
    if (!Number.isFinite(ratingNum) || ratingNum < 1 || ratingNum > 5) {
      setSnackbar({
        isOpen: true,
        message: "Rating must be between 1 and 5",
        type: "error",
      });
      return;
    }
    if (!editTestimonialDescription.trim() || !editTestimonialAuthor.trim()) {
      setSnackbar({
        isOpen: true,
        message: "Author and description are required",
        type: "error",
      });
      return;
    }

    try {
      setTestimonialUpdating(true);
      await axios.patch(
        `https://api.whoffers.com/api/testimonials/${editingTestimonial.id}`,
        {
          rating: String(ratingNum),
          description: editTestimonialDescription.trim(),
          author: editTestimonialAuthor.trim(),
          is_active: editTestimonialIsActive ? 1 : 0,
        },
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          : {
              headers: { "Content-Type": "application/json" },
            },
      );

      setSnackbar({
        isOpen: true,
        message: "Testimonial updated successfully!",
        type: "success",
      });
      setEditingTestimonial(null);
      fetchTestimonials(Number(testimonialsVertical.id));
    } catch (error: any) {
      console.error("Error updating testimonial:", error);
      setSnackbar({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to update testimonial",
        type: "error",
      });
    } finally {
      setTestimonialUpdating(false);
    }
  };

  const handleDeleteTestimonial = async (testimonialId: number) => {
    if (!testimonialsVertical?.id || !testimonialId) return;
    const ok = window.confirm("Are you sure you want to delete this testimonial?");
    if (!ok) return;

    const idKey = String(testimonialId);
    try {
      setTestimonialDeletingIds((prev) => ({ ...prev, [idKey]: true }));
      await axios.delete(
        `https://api.whoffers.com/api/testimonials/${testimonialId}`,
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );

      setSnackbar({
        isOpen: true,
        message: "Testimonial deleted successfully!",
        type: "success",
      });
      fetchTestimonials(Number(testimonialsVertical.id));
    } catch (error: any) {
      console.error("Error deleting testimonial:", error);
      setSnackbar({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to delete testimonial",
        type: "error",
      });
    } finally {
      setTestimonialDeletingIds((prev) => ({ ...prev, [idKey]: false }));
    }
  };

  console.log(subverticals, "Subs");
  console.log(bannerName, "Banner Name");
  console.log(bannerVariant, "Banner Variant");
  return (
    <Container>
      <PageTitle>Verticals</PageTitle>

      <CreateButton onClick={handleCreate} disabled={saving}>
        {saving ? "Creating..." : "+ Create Vertical"}
      </CreateButton>

      <Tile>
        <TileHeader>
          <h2>ID</h2>
          <h2>Name</h2>
          <h2>Actions</h2>
        </TileHeader>

        <TileSection>
          {loading ? (
            // Skeleton loading state
            Array.from({ length: 5 }).map((_, index) => (
              <TileRow key={index}>
                <SkeletonElement />
                <SkeletonElement />
                <SkeletonElement />
              </TileRow>
            ))
          ) : verticals.length > 0 ? (
            verticals.map((vertical) => (
              <TileRow key={vertical.id}>
                <TileElement>{vertical.id}</TileElement>
                <TileElement>
                  {capitalizeFirst(vertical.name || "N/A")}
                </TileElement>
                <TileElement>
                  <ActionButton onClick={() => handleOpenTestimonials(vertical)}>
                    Testimonials
                  </ActionButton>
                  <ActionButton
                    onClick={() => handleManageBannerClick(vertical)}
                  >
                    Manage Banner
                  </ActionButton>
                  <ActionButton
                    $variant="edit"
                    onClick={() => handleEditClick(vertical)}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton
                    $variant="delete"
                    onClick={() => handleDeleteClick(vertical)}
                  >
                    Delete
                  </ActionButton>
                </TileElement>
              </TileRow>
            ))
          ) : (
            <TileRow>
              <TileElement style={{ width: "100%", textAlign: "center" }}>
                No verticals found
              </TileElement>
            </TileRow>
          )}
        </TileSection>
      </Tile>

      {/* Zip Codes Modal */}
      {showZipModal && zipVertical && (
        <ModalOverlay
          onClick={() => {
            setShowZipModal(false);
            setZipVertical(null);
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>
                Manage Zipcodes for {capitalizeFirst(zipVertical.name || "")}
              </h2>
              <CloseButton
                onClick={() => {
                  setShowZipModal(false);
                  setZipVertical(null);
                }}
              >
                ×
              </CloseButton>
            </ModalHeader>
            <form onSubmit={handleZipCodesUpload}>
              <ModalFormGroup>
                <ModalLabel htmlFor="csvFileInput">
                  Upload CSV File *
                </ModalLabel>
                <FileInput
                  type="file"
                  id="csvFileInput"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setCsvFile(file);
                    }
                  }}
                  required
                />
              </ModalFormGroup>
              <ModalButtonGroup>
                <ModalButton
                  type="submit"
                  disabled={zipcodesUploading || !csvFile}
                >
                  {zipcodesUploading ? "Uploading..." : "Upload CSV"}
                </ModalButton>
              </ModalButtonGroup>
            </form>

            {zipcodesLoading ? (
              <div
                style={{
                  marginTop: "1rem",
                  textAlign: "center",
                  color: "#666",
                }}
              >
                Loading zip codes...
              </div>
            ) : zipcodes.length > 0 ? (
              <>
                <ZipCodesList>
                  {zipcodes.map((zipcode: any, index: number) => (
                    <ZipCodeItem key={index}>
                      {zipcode.zip_code ||
                        zipcode.zipcode ||
                        zipcode.code ||
                        zipcode}
                    </ZipCodeItem>
                  ))}
                </ZipCodesList>
                {zipcodesTotalPages >= 1 && zipcodes.length > 0 && (
                  <PaginationContainer>
                    <PaginationButton
                      onClick={() => handleZipCodesPageChange(zipcodesPage - 1)}
                      disabled={zipcodesPage === 1}
                      $disabled={zipcodesPage === 1}
                    >
                      Previous
                    </PaginationButton>
                    {Array.from(
                      { length: zipcodesTotalPages },
                      (_, i) => i + 1,
                    ).map((pageNum) => (
                      <PageNumberButton
                        key={pageNum}
                        $active={zipcodesPage === pageNum}
                        onClick={() => handleZipCodesPageChange(pageNum)}
                      >
                        {pageNum}
                      </PageNumberButton>
                    ))}
                    <PaginationButton
                      onClick={() => handleZipCodesPageChange(zipcodesPage + 1)}
                      disabled={zipcodesPage === zipcodesTotalPages}
                      $disabled={zipcodesPage === zipcodesTotalPages}
                    >
                      Next
                    </PaginationButton>
                  </PaginationContainer>
                )}
                <ModalButtonGroup
                  style={{
                    marginTop: "1.5rem",
                    borderTop: "1px solid #eee",
                    paddingTop: "1rem",
                  }}
                >
                  <ModalButton
                    type="button"
                    disabled={zipcodesDeleting || zipcodes.length === 0}
                    onClick={handleDeleteZipCodes}
                    style={{ backgroundColor: "#dc3545", color: "white" }}
                  >
                    {zipcodesDeleting ? "Deleting..." : "Delete all zip codes"}
                  </ModalButton>
                </ModalButtonGroup>
              </>
            ) : (
              <p
                style={{
                  marginTop: "1rem",
                  color: "#666",
                  textAlign: "center",
                }}
              >
                No zip codes found. Upload a CSV file to add zip codes.
              </p>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Testimonials Modal */}
      {showTestimonialsModal && testimonialsVertical && (
        <ModalOverlay
          onClick={() => {
            setShowTestimonialsModal(false);
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>
                Testimonials — {capitalizeFirst(testimonialsVertical.name || "")}
              </h2>
              <CloseButton onClick={() => setShowTestimonialsModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            {testimonialsLoading ? (
              <div style={{ color: "#666", textAlign: "center" }}>
                Loading testimonials...
              </div>
            ) : testimonials.length > 0 ? (
              <TestimonialsList>
                {testimonials.map((t: any, idx: number) => (
                  <TestimonialCard key={t?.id ?? `${idx}`}>
                    <TestimonialMetaRow>
                      <span>Rating: {t?.rating ?? "N/A"}</span>
                      <span>Author: {t?.author ?? "N/A"}</span>
                      <Badge $tone={t?.is_active ? "success" : "muted"}>
                        {t?.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TestimonialMetaRow>
                    <div style={{ color: "#333", fontWeight: 600 }}>
                      {t?.description ?? "N/A"}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <ActionButton
                        type="button"
                        $variant="edit"
                        onClick={() => handleEditTestimonialClick(t)}
                        disabled={
                          testimonialUpdating || !!testimonialDeletingIds[String(t?.id)]
                        }
                      >
                        Edit
                      </ActionButton>
                      <ActionButton
                        type="button"
                        $variant="delete"
                        onClick={() => handleDeleteTestimonial(Number(t?.id))}
                        disabled={!!testimonialDeletingIds[String(t?.id)]}
                        style={{ marginRight: 0 }}
                      >
                        {testimonialDeletingIds[String(t?.id)]
                          ? "Deleting..."
                          : "Delete"}
                      </ActionButton>
                    </div>
                  </TestimonialCard>
                ))}
              </TestimonialsList>
            ) : (
              <div style={{ color: "#666", textAlign: "center" }}>
                No testimonials found for this vertical.
              </div>
            )}

            {editingTestimonial && (
              <div
                style={{
                  borderTop: "1px solid #eee",
                  paddingTop: "1rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <div style={{ fontWeight: 800, color: "#013566" }}>
                  Edit testimonial (ID: {editingTestimonial.id})
                </div>
                <form onSubmit={handleUpdateTestimonial}>
                  <ModalFormGroup>
                    <ModalLabel htmlFor="editTestimonialRating">
                      Rating (1-5) *
                    </ModalLabel>
                    <ModalInput
                      id="editTestimonialRating"
                      type="number"
                      min={1}
                      max={5}
                      value={editTestimonialRating}
                      onChange={(e) => setEditTestimonialRating(e.target.value)}
                      required
                    />

                    <ModalLabel htmlFor="editTestimonialAuthor">Author *</ModalLabel>
                    <ModalInput
                      id="editTestimonialAuthor"
                      type="text"
                      value={editTestimonialAuthor}
                      onChange={(e) => setEditTestimonialAuthor(e.target.value)}
                      required
                    />

                    <ModalLabel htmlFor="editTestimonialDescription">
                      Description *
                    </ModalLabel>
                    <TextArea
                      id="editTestimonialDescription"
                      value={editTestimonialDescription}
                      onChange={(e) => setEditTestimonialDescription(e.target.value)}
                      required
                    />

                    <CheckboxRow>
                      <input
                        type="checkbox"
                        checked={editTestimonialIsActive}
                        onChange={() => setEditTestimonialIsActive((v) => !v)}
                      />
                      <span>Active</span>
                    </CheckboxRow>
                  </ModalFormGroup>

                  <ModalButtonGroup>
                    <ModalButton
                      type="button"
                      $variant="secondary"
                      onClick={() => setEditingTestimonial(null)}
                      disabled={testimonialUpdating}
                    >
                      Cancel edit
                    </ModalButton>
                    <ModalButton type="submit" disabled={testimonialUpdating}>
                      {testimonialUpdating ? "Saving..." : "Save changes"}
                    </ModalButton>
                  </ModalButtonGroup>
                </form>
              </div>
            )}

            {!editingTestimonial && (
              <div
                style={{
                  borderTop: "1px solid #eee",
                  paddingTop: "1rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <div style={{ fontWeight: 800, color: "#013566" }}>
                  Add testimonial
                </div>
                <form onSubmit={handleCreateTestimonial}>
                  <ModalFormGroup>
                    <ModalLabel htmlFor="testimonialRating">
                      Rating (1-5) *
                    </ModalLabel>
                    <ModalInput
                      id="testimonialRating"
                      type="number"
                      min={1}
                      max={5}
                      value={testimonialRating}
                      onChange={(e) => setTestimonialRating(e.target.value)}
                      required
                    />

                    <ModalLabel htmlFor="testimonialAuthor">Author *</ModalLabel>
                    <ModalInput
                      id="testimonialAuthor"
                      type="text"
                      value={testimonialAuthor}
                      onChange={(e) => setTestimonialAuthor(e.target.value)}
                      required
                    />

                    <ModalLabel htmlFor="testimonialDescription">
                      Description *
                    </ModalLabel>
                    <TextArea
                      id="testimonialDescription"
                      value={testimonialDescription}
                      onChange={(e) => setTestimonialDescription(e.target.value)}
                      required
                    />

                    <CheckboxRow>
                      <input
                        type="checkbox"
                        checked={testimonialIsActive}
                        onChange={() => setTestimonialIsActive((v) => !v)}
                      />
                      <span>Active</span>
                    </CheckboxRow>
                  </ModalFormGroup>

                  <ModalButtonGroup>
                    <ModalButton
                      type="button"
                      $variant="secondary"
                      onClick={() => setShowTestimonialsModal(false)}
                      disabled={testimonialSaving}
                    >
                      Close
                    </ModalButton>
                    <ModalButton type="submit" disabled={testimonialSaving}>
                      {testimonialSaving ? "Saving..." : "Create"}
                    </ModalButton>
                  </ModalButtonGroup>
                </form>
              </div>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Manage Banner Modal */}
      {showBannerModal && bannerVertical && (
        <ModalOverlay
          onClick={() => {
            setShowBannerModal(false);
            setBannerVertical(null);
            setAddingBanner(false);
            setEditingBanner(false);
            setEditingSubvertical(null);
            setBannerName("");
            setBannerVariant("");
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>
                Manage Banner — {capitalizeFirst(bannerVertical.name || "")}
              </h2>
              <CloseButton
                onClick={() => {
                  setShowBannerModal(false);
                  setBannerVertical(null);
                  setAddingBanner(false);
                  setEditingBanner(false);
                  setEditingSubvertical(null);
                  setBannerName("");
                  setBannerVariant("");
                }}
              >
                ×
              </CloseButton>
            </ModalHeader>
            {subverticalsLoading ? (
              <TileSection>
                {Array.from({ length: 4 }).map((_, i) => (
                  <TileRow key={i}>
                    <SkeletonElement />
                    <SkeletonElement />
                  </TileRow>
                ))}
              </TileSection>
            ) : subverticals.length > 0 ? (
              <TileSection>
                {subverticals.map((sv: any) => (
                  <TileRow key={sv.id}>
                    <TileElement>{sv.id}</TileElement>
                    <TileElement>
                      {capitalizeFirst(sv.name ?? "N/A")}
                    </TileElement>
                    <TileElement>{sv?.banner}</TileElement>
                    <TileElement>
                      <ModalButton
                        $variant="secondary"
                        onClick={() => handleEditBannerClick(sv)}
                      >
                        Edit
                      </ModalButton>
                    </TileElement>
                  </TileRow>
                ))}
              </TileSection>
            ) : (
              <p style={{ margin: 0, color: "#666" }}>
                No banners for this vertical.
              </p>
            )}
            <ModalButton
              disabled={addingBanner || bannerSaving}
              onClick={handleAddBannerButtonClick}
            >
              Add Banner
            </ModalButton>

            {addingBanner && (
              <form
                onSubmit={
                  editingBanner ? handleEditBannerSubmit : handleAddBannerSubmit
                }
              >
                <ModalFormGroup>
                  <ModalLabel htmlFor="bannerName">Banner Name *</ModalLabel>
                  <ModalInput
                    type="text"
                    id="bannerName"
                    required
                    autoFocus
                    value={bannerName}
                    onChange={(e) => setBannerName(e.target.value)}
                  />
                  <ModalLabel htmlFor="bannerVariant">
                    Banner Variant *
                  </ModalLabel>
                  <ModalInput
                    type="text"
                    id="bannerVariant"
                    required
                    value={bannerVariant}
                    onChange={(e) => setBannerVariant(e.target.value)}
                  />
                </ModalFormGroup>

                <ModalButton
                  type="submit"
                  style={{ marginTop: "1rem" }}
                  disabled={bannerSaving}
                >
                  {bannerSaving ? "Saving..." : "Save"}
                </ModalButton>
              </form>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit Modal */}
      {showEditModal && editingVertical && (
        <ModalOverlay onClick={() => setShowEditModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Edit Vertical</h2>
              <CloseButton onClick={() => setShowEditModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            <form onSubmit={handleEditSubmit}>
              <ModalFormGroup>
                <ModalLabel htmlFor="editName">Name *</ModalLabel>
                <ModalInput
                  type="text"
                  id="editName"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter vertical name"
                  required
                  autoFocus
                />
              </ModalFormGroup>
              <ModalButtonGroup>
                <ModalButton
                  type="button"
                  $variant="secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingVertical(null);
                    setEditName("");
                  }}
                  disabled={saving}
                >
                  Cancel
                </ModalButton>
                <ModalButton type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </ModalButton>
              </ModalButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && deletingVertical && (
        <ConfirmDialog onClick={handleDeleteCancel}>
          <ConfirmContent onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: 0, color: "#013566" }}>Confirm Delete</h2>
            <ConfirmText>
              Are you sure you want to delete the vertical "
              {capitalizeFirst(deletingVertical.name)}"? This action cannot be
              undone.
            </ConfirmText>
            <ModalButtonGroup>
              <ModalButton
                type="button"
                $variant="secondary"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </ModalButton>
              <ModalButton
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                style={{ backgroundColor: "#dc3545", color: "white" }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </ModalButton>
            </ModalButtonGroup>
          </ConfirmContent>
        </ConfirmDialog>
      )}
    </Container>
  );
};

export default Verticals;
